/**
 * Estado del árbol de un reporte: ramas abiertas y controles de detalle.
 *
 * Las ramas abiertas se guardan por reporte y período en `sessionStorage`: si
 * el contador abre una cuenta, entra al Libro Mayor y vuelve, el árbol sigue
 * donde lo dejó. Perder ese estado obliga a rehacer cinco clics para volver al
 * mismo sitio, que es la clase de fricción que hace que nadie use el detalle.
 */

import { useCallback, useEffect, useState } from "react";
import type { TreeMode } from "../api/types";

export interface TreeState {
  mode: TreeMode;
  setMode: (mode: TreeMode) => void;
  fullChain: boolean;
  setFullChain: (value: boolean) => void;
  showZero: boolean;
  setShowZero: (value: boolean) => void;
  expanded: Set<string>;
  toggle: (nodeId: string) => void;
  expandAll: (nodeIds: Iterable<string>) => void;
  collapseAll: () => void;
  /** Siembra inicial; no hace nada si ya había algo guardado o ya se sembró. */
  seed: (nodeIds: Iterable<string>) => void;
}

export function useTreeState(key: string, defaultMode: TreeMode = "formal"): TreeState {
  const [mode, setMode] = useState<TreeMode>(defaultMode);
  const [fullChain, setFullChain] = useState(false);
  const [showZero, setShowZero] = useState(false);
  const inicial = useState(() => leer(key))[0];
  const [expanded, setExpanded] = useState<Set<string>>(inicial.expanded);
  const [claveVigente, setClaveVigente] = useState(key);
  // Una sesión guardada VACÍA es una decisión del contador —colapsó el árbol—,
  // no la ausencia de decisión. Distinguirlas es lo que impide que la siembra
  // le reabra las ramas que acababa de cerrar.
  const [sembrado, setSembrado] = useState(inicial.stored);
  // Nada se escribe hasta que hay algo que registrar. Sin esta guarda, el
  // primer render guardaba un conjunto vacío, y al volver a la pantalla esa
  // escritura se leía como «el contador colapsó el árbol» y bloqueaba la
  // siembra: la vista quedaba en cinco filas para siempre.
  const [tocado, setTocado] = useState(false);

  // Cambiar de reporte o de período recarga las ramas abiertas de ESE contexto.
  // Se ajusta durante el render, no en un efecto: con un efecto habría un
  // render intermedio mostrando el árbol del período anterior.
  if (key !== claveVigente) {
    const recien = leer(key);
    setClaveVigente(key);
    setExpanded(recien.expanded);
    setSembrado(recien.stored);
    setTocado(false);
  }

  useEffect(() => {
    if (tocado) guardar(key, expanded);
  }, [key, expanded, tocado]);

  const toggle = useCallback((nodeId: string) => {
    setTocado(true);
    setExpanded((previo) => {
      const siguiente = new Set(previo);
      if (siguiente.has(nodeId)) siguiente.delete(nodeId);
      else siguiente.add(nodeId);
      return siguiente;
    });
  }, []);

  const expandAll = useCallback((nodeIds: Iterable<string>) => {
    setTocado(true);
    setExpanded(new Set(nodeIds));
  }, []);
  const collapseAll = useCallback(() => {
    setTocado(true);
    setExpanded(new Set());
  }, []);

  // La siembra NO marca el estado como tocado: es un valor por defecto, no una
  // decisión del contador, y no debe persistirse como si lo fuera.
  const seed = useCallback(
    (nodeIds: Iterable<string>) => {
      if (sembrado) return;
      setSembrado(true);
      setExpanded(new Set(nodeIds));
    },
    [sembrado],
  );

  return {
    mode,
    setMode,
    fullChain,
    setFullChain,
    showZero,
    setShowZero,
    expanded,
    toggle,
    expandAll,
    collapseAll,
    seed,
  };
}

// `sessionStorage` puede lanzar —ventana privada, cookies bloqueadas—: el árbol
// tiene que dibujarse igual, solo que sin recordar nada.
function leer(key: string): { expanded: Set<string>; stored: boolean } {
  try {
    const crudo = sessionStorage.getItem(`tree:${key}`);
    if (crudo === null) return { expanded: new Set(), stored: false };
    return { expanded: new Set(JSON.parse(crudo) as string[]), stored: true };
  } catch {
    return { expanded: new Set(), stored: false };
  }
}

function guardar(key: string, expanded: Set<string>): void {
  try {
    sessionStorage.setItem(`tree:${key}`, JSON.stringify([...expanded]));
  } catch {
    // Sin almacenamiento el árbol funciona; solo no recuerda entre pantallas.
  }
}
